import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import ClaudeTerminal from '../components/ClaudeTerminal';
import TwoFactorEnrollModal from '../components/TwoFactorEnrollModal';
import TwoFactorVerifyModal from '../components/TwoFactorVerifyModal';
import api from '../services/api';

const Terminal = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [twoFactorStatus, setTwoFactorStatus] = useState({
    enabled: false,
    recentlyVerified: false,
    loading: true
  });
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);

  // useEffect must be called before any early returns
  useEffect(() => {
    if (user && user.role === 'master_admin') {
      checkTwoFactorStatus();
    }
  }, [user]);

  // Protect route - only master_admin can access
  if (!user || user.role !== 'master_admin') {
    return <Navigate to="/" replace />;
  }

  const checkTwoFactorStatus = async () => {
    try {
      const response = await api.get('/2fa/status');
      if (response.data.success) {
        setTwoFactorStatus({
          enabled: response.data.enabled,
          recentlyVerified: response.data.recentlyVerified,
          loading: false
        });
      }
    } catch (error) {
      console.error('Error checking 2FA status:', error);
      setTwoFactorStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const handleAccessTerminal = () => {
    if (!twoFactorStatus.enabled) {
      // Prompt to enroll
      setShowEnrollModal(true);
    } else if (!twoFactorStatus.recentlyVerified) {
      // Prompt to verify
      setShowVerifyModal(true);
    } else {
      // Open terminal directly
      setShowTerminal(true);
    }
  };

  const handleEnrollSuccess = () => {
    checkTwoFactorStatus();
    // After enrollment, show verify modal
    setTimeout(() => {
      setShowVerifyModal(true);
    }, 500);
  };

  const handleVerifySuccess = () => {
    setTwoFactorStatus(prev => ({ ...prev, recentlyVerified: true }));
    setShowTerminal(true);
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable Two-Factor Authentication? This will make your account less secure.')) {
      return;
    }

    // Require current 2FA token to disable (security measure)
    const token = window.prompt('Enter your current 2FA code from the authenticator app to confirm:');
    if (!token) {
      return;
    }

    try {
      const response = await api.post('/2fa/disable', { token });
      if (response.data.success) {
        checkTwoFactorStatus();
        alert('2FA has been disabled');
      }
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      const message = error.response?.data?.message || 'Failed to disable 2FA';
      alert(message);
    }
  };

  if (showTerminal) {
    return (
      <div className="h-screen w-full">
        <ClaudeTerminal onClose={() => setShowTerminal(false)} />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600">{t('cms.terminal.title')}</h1>
          <p className="text-gray-600">{t('cms.terminal.subtitle')}</p>
        </div>
      </div>

      {/* 2FA Status Card */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Security Status</h2>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            twoFactorStatus.enabled
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {twoFactorStatus.loading ? 'Checking...' : twoFactorStatus.enabled ? '2FA Enabled' : '2FA Not Enabled'}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-start">
            <svg className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${
              twoFactorStatus.enabled ? 'text-green-500' : 'text-gray-400'
            }`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-medium text-gray-900">Two-Factor Authentication</p>
              <p className="text-sm text-gray-600">
                {twoFactorStatus.enabled
                  ? 'Your account is protected with 2FA'
                  : t('cms.terminal.requiresTwoFa')}
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <svg className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${
              twoFactorStatus.recentlyVerified ? 'text-green-500' : 'text-gray-400'
            }`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-medium text-gray-900">Session Verification</p>
              <p className="text-sm text-gray-600">
                {twoFactorStatus.recentlyVerified
                  ? 'Verified (valid for 30 minutes)'
                  : 'Verification required'}
              </p>
            </div>
          </div>
        </div>

        {twoFactorStatus.enabled && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={handleDisable2FA}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Disable Two-Factor Authentication
            </button>
          </div>
        )}
      </div>

      {/* Terminal Access Card */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-start mb-4">
          <div className="bg-secondary-500 rounded-full p-3 mr-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Access Terminal</h3>
            <p className="text-gray-700 mb-4">
              Launch a secure browser-based terminal for quick fixes.
              Perfect for making quick changes to your site from your mobile device.
            </p>
            <div className="space-y-2 mb-4">
              <div className="flex items-center text-sm text-gray-600">
                <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Full terminal CLI access
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Working directory configured for your deployment
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                15-minute session timeout for security
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Command audit logging enabled
              </div>
            </div>
            <button
              onClick={handleAccessTerminal}
              disabled={twoFactorStatus.loading}
              className="btn btn-primary px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {twoFactorStatus.enabled ? 'Launch Terminal' : 'Setup 2FA & Launch'}
            </button>
          </div>
        </div>
      </div>

      {/* Security Notice */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm font-medium text-yellow-800">Security Notice</p>
            <p className="text-sm text-yellow-700 mt-1">
              The terminal provides full system access. All commands are logged for security.
              Only use this feature on trusted devices.
            </p>
          </div>
        </div>
      </div>

      {/* Modals */}
      <TwoFactorEnrollModal
        isOpen={showEnrollModal}
        onClose={() => setShowEnrollModal(false)}
        onSuccess={handleEnrollSuccess}
      />

      <TwoFactorVerifyModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        onSuccess={handleVerifySuccess}
      />
    </div>
  );
};

export default Terminal;
